import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { cloneDeep } from 'lodash';
import { PatientCacheService } from '../../../requester/patient/patient-cache.services';
import { PatientService } from '../../../requester/patient/patient.service';
import { UserProfileService } from '../../../user-profile/user-profile.service';
import { ExpertRequest } from '../models/expert-request.models';
import { MedicalOpinion } from '../models/medical-opinion.models';
import {
    convertPageToPagination,
    IExpertRequestFilter,
    IExpertStats,
    IMedicalOpinionCreate,
    IRequestPagination
} from '../types/expert-request.types';
import { Roles } from 'utils/roles';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FCMService } from 'app/core/fcm/fcm.service';
import { AppActivityService } from 'app/core/app-activity.service';

/**
 * Expert Request service - UNIFIED SERVICE FOR BOTH MODULES
 * 
 * Handles both:
 * - Pending Requests (WAITING status, checkout functionality)
 * - Expert Contributions (IN_PROGRESS, COMPLETED, with opinion management)
 */
@Injectable({ providedIn: 'root' })
export class ExpertRequestService {
    private _httpClient = inject(HttpClient);
    private _userProfileService = inject(UserProfileService);
    private _patientService = inject(PatientService);
    private _patientCacheService = inject(PatientCacheService);
    private _fcmService = inject(FCMService);
    private _appActivity = inject(AppActivityService);

    // Unified state for both modules
    private _allRequests = signal<ExpertRequest[]>([]);
    private _currentRequest = signal<ExpertRequest | null>(null);
    private _allOpinions = signal<MedicalOpinion[]>([]);
    private _currentOpinion = signal<MedicalOpinion | null>(null);
    private _isLoading = signal<boolean>(false);
    private _error = signal<string | null>(null);
    private _currentFilter = signal<IExpertRequestFilter>({ sort: 'zcreationTimestamp:desc' });
    private _pagination = signal<IRequestPagination | null>(null);

    private _requestsWithDraft = signal<Set<string>>(new Set());
    public readonly requestsWithDraft = this._requestsWithDraft.asReadonly();

    /**
     * Which of the two lists currently occupies `_allRequests`.
     *
     * Both screens share that one signal, so an automatic refresh has to reload the list the user
     * is actually looking at. This used to be guessed from `_currentFilter().f_status`, but that
     * flag is only present when a caller happens to pass it - the route resolver and the "reset
     * filters" path both call the loaders with no status at all. The guess therefore fell through
     * to the contributions branch and overwrote the pending list with the wrong data.
     */
    private _activeList = signal<'pending' | 'contributions' | null>(null);

    // Public readonly signals
    public readonly requests = this._allRequests.asReadonly();
    public readonly currentRequest = this._currentRequest.asReadonly();
    public readonly opinions = this._allOpinions.asReadonly();
    public readonly currentOpinion = this._currentOpinion.asReadonly();
    public readonly isLoading = this._isLoading.asReadonly();
    public readonly error = this._error.asReadonly();
    public readonly currentFilter = this._currentFilter.asReadonly();
    public readonly pagination = this._pagination.asReadonly();

    // Computed stats
    public readonly requestStats = computed(() => {
        const requests = this._allRequests();
        const stats: IExpertStats = {
            totalRequests: requests.length,
            unreadCount: requests.filter(r => !r.isRead).length,
            waitingCount: requests.filter(r => r.status === 'WAITING').length,
            inProgressCount: requests.filter(r => r.status === 'IN_PROGRESS').length,
            completedCount: requests.filter(r => r.status === 'COMPLETED').length,
            byRequestType: {}
        };

        requests.forEach(request => {
            stats.byRequestType[request.requestType] = (stats.byRequestType[request.requestType] || 0) + 1;
        });

        return stats;
    });

    public readonly hasRequests = computed(() => this._allRequests().length > 0);

    constructor() {
        // Default load for current module

        // Subscribe to FCM notifications to auto-refresh the requests list
        this._fcmService.onMessageReceived$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                console.log('[ExpertRequestService] FCM message received, reloading requests...');
                this.reloadActiveList();
            });

        // Same reason as the requester side: a missed push must not leave the expert looking at
        // requests that have already been handled.
        this._appActivity.resumed$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.reloadActiveList();
            });
    }

    /**
     * Re-reads whichever list is currently on screen, keeping its filters.
     *
     * Does nothing before either list has been loaded once: there would be no way to tell which
     * one to fetch, and guessing would replace `_allRequests` with the other screen's data.
     */
    private reloadActiveList(): void {
        const filter = this._currentFilter();

        switch (this._activeList()) {
            case 'pending':
                this.loadPendingRequests(filter);
                break;
            case 'contributions':
                this.loadExpertContributions(filter);
                break;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Expert ID Management
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get current expert entityId from user profile
     */
    private async getExpertId(): Promise<string> {
        const clinicianProfile = this._userProfileService.clinicianProfile();
        if (clinicianProfile?.entityId) {
            return clinicianProfile.entityId;
        }

        try {
            const profile = await this._userProfileService.getProfile();
            if (profile?.entityId) {
                return profile.entityId;
            }
        } catch (error) {
            console.warn('Could not load clinician profile:', error);
        }

        throw new Error('Expert ID not available - clinician profile not loaded');
    }

    /**
     * Get current expert roles for filtering
     */
    private async getExpertRoles(): Promise<string[]> {
        const clinicianProfile = this._userProfileService.clinicianProfile();
        if (clinicianProfile?.roles) {
            // Filter out generic roles as per business logic
            return clinicianProfile.roles.filter(
              (role) =>
                !role.includes(Roles.ROLE_DIPITA_EXPERT) &&
                !role.includes(Roles.ROLE_DIPITA_REQUESTER)
            );
        }

        try {
            const profile = await this._userProfileService.getProfile();
            if (profile?.roles) {
                return profile.roles.filter(
                  (role) =>
                    !role.includes(Roles.ROLE_DIPITA_EXPERT) &&
                    !role.includes(Roles.ROLE_DIPITA_REQUESTER)
                );
            }
        } catch (error) {
            console.warn('Could not load expert roles:', error);
        }

        return [];
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Pending Requests (WAITING status)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load pending requests (WAITING status with expert roles filtering)
     */
    // async loadPendingRequests(filter: IExpertRequestFilter = {}): Promise<void> {
    //     const mergedFilter = { sort: 'zcreationTimestamp:desc', ...filter };
    //     this._isLoading.set(true);
    //     this._error.set(null);
    
    //     try {
    //         const expertRoles = await this.getExpertRoles();
            
    //         let params = new HttpParams()
    //             .set('status', 'WAITING');
    
    //         // Add roles filtering - only medical speciality roles
    //         if (expertRoles.length > 0) {
    //             expertRoles.forEach(role => {
    //                 params = params.append('roles', role);
    //             });
    //         } else {
    //             // console.warn('No medical speciality roles found - no pending requests should be visible');
    //             this._allRequests.set([]);
    //             this._pagination.set(null);
    //             this._currentFilter.set(mergedFilter);
    //             return;
    //         }
    
    //         // Add other filters
    //         if (mergedFilter.search) params = params.set('search', mergedFilter.search);
    //         if (mergedFilter.f_request_type) params = params.set('f_request_type', mergedFilter.f_request_type);
    //         if (mergedFilter.f_speciality) params = params.set('expertisearea', mergedFilter.f_speciality);
    //         if (mergedFilter.f_reading !== undefined) params = params.set('f_reading', mergedFilter.f_reading.toString());
    //         if (mergedFilter.sort) params = params.set('sort', mergedFilter.sort);
    //         if (mergedFilter.page !== undefined) params = params.set('page', mergedFilter.page.toString());
    //         if (mergedFilter.per_page !== undefined) params = params.set('per_page', mergedFilter.per_page.toString());
    
    //         // console.log('Loading pending requests with params (no expertId):', params.toString());
    
    //         const response = await this._httpClient
    //             .get<any>('aplt_backend/api/requests', { params })
    //             .toPromise();
    
    //         if (response) {
    //             const requests = response.content?.map((item: any) => this.mapRequestFromAPI(item)) || [];
    //             const requestsWithPatients = await Promise.all(
    //                 requests.map(request => this.loadPatientDataForRequest(request))
    //             );
                
    //             // console.log(`Loaded ${requestsWithPatients.length} pending requests`);
                
    //             this._allRequests.set(requestsWithPatients);
    //             const pagination = convertPageToPagination(response);
    //             this._pagination.set(pagination);
    //             this._currentFilter.set(mergedFilter);
    //         }
    //     } catch (error) {
    //         // console.error('Error loading pending requests:', error);
    //         this._error.set(error instanceof Error ? error.message : 'Failed to load pending requests');
    //     } finally {
    //         this._isLoading.set(false);
    //     }
    // }

    async loadPendingRequests(filter: IExpertRequestFilter = {}): Promise<void> {
        const mergedFilter = { sort: 'zcreationTimestamp:desc', ...filter };
        this._activeList.set('pending');
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            const expertRoles = await this.getExpertRoles();
            
            let params = new HttpParams()
                .set('status', 'WAITING');
    
            // Add roles filtering - only medical speciality roles
            if (expertRoles.length > 0) {
                expertRoles.forEach(role => {
                    params = params.append('roles', role);
                });
            } else {
                this._allRequests.set([]);
                this._pagination.set(null);
                this._currentFilter.set(mergedFilter);
                return;
            }
    
            // Add other filters
            if (mergedFilter.search) params = params.set('search', mergedFilter.search);
            if (mergedFilter.f_speciality) params = params.set('expertisearea', mergedFilter.f_speciality);
            if (mergedFilter.f_request_type) params = params.set('f_request_type', mergedFilter.f_request_type);
            if (mergedFilter.f_reading !== undefined) params = params.set('f_reading', mergedFilter.f_reading.toString());
            if (mergedFilter.sort) params = params.set('sort', mergedFilter.sort);
            if (mergedFilter.page !== undefined) params = params.set('page', mergedFilter.page.toString());
            if (mergedFilter.per_page !== undefined) params = params.set('per_page', mergedFilter.per_page.toString());
    
            const response = await this._httpClient
                .get<any>('aplt_backend/api/requests', { params })
                .toPromise();
    
            if (response) {
                const requests = response.content?.map((item: any) => this.mapRequestFromAPI(item)) || [];
                const requestsWithPatients = await Promise.all(
                    requests.map(request => this.loadPatientDataForRequest(request))
                );
                
                // Filter expert own requests
                const filteredRequests = await this.filterOutOwnRequests(requestsWithPatients);
                
                this._allRequests.set(filteredRequests);
                
                const pagination = convertPageToPagination(response);
                if (filteredRequests.length !== requestsWithPatients.length) {
                    const removedCount = requestsWithPatients.length - filteredRequests.length;
                    pagination.totalItems = Math.max(0, pagination.totalItems - removedCount);
                    pagination.numberOfElements = filteredRequests.length;
                    
                    if (filteredRequests.length < pagination.pageSize && pagination.currentPage > 1) {
                        pagination.isLast = true;
                    }
                }
                
                this._pagination.set(pagination);
                this._currentFilter.set(mergedFilter);
            }
        } catch (error) {
            // this._error.set(error instanceof Error ? error.message : 'Failed to load pending requests');
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
    * Filter out requests created by the current expert (workaround)
    * NOTE: This is a temporary solution that affects pagination accuracy
    */
    private async filterOutOwnRequests(requests: ExpertRequest[]): Promise<ExpertRequest[]> {
        try {
            const expertId = await this.getExpertId();
            const filtered = requests.filter(request => request.clinicianId !== expertId);
            
            // Log for debugging if requests were filtered out
            if (filtered.length !== requests.length) {
                console.warn(`Filtered out ${requests.length - filtered.length} own requests from pending list`);
            }
            
            return filtered;
        } catch (error) {
            console.warn('Could not filter own requests:', error);
            return requests; // Return original list if filtering fails
        }
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ Expert Contributions (IN_PROGRESS, COMPLETED)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load expert contributions (requests handled by current expert)
     */
    async loadExpertContributions(filter: IExpertRequestFilter = {}): Promise<void> {
        const mergedFilter = { sort: 'zcreationTimestamp:desc', ...filter };
        this._activeList.set('contributions');
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const expertId = await this.getExpertId();
            
            let params = new HttpParams()
                .set('expertId', expertId);

            // Add filters
            if (mergedFilter.search) params = params.set('search', mergedFilter.search);
            if (mergedFilter.f_status) params = params.set('f_status', mergedFilter.f_status);
            if (mergedFilter.f_request_type) params = params.set('f_request_type', mergedFilter.f_request_type);
            if (mergedFilter.f_reading !== undefined) params = params.set('f_reading', mergedFilter.f_reading.toString());
            if (mergedFilter.sort) params = params.set('sort', mergedFilter.sort);
            if (mergedFilter.page !== undefined) params = params.set('page', mergedFilter.page.toString());
            if (mergedFilter.per_page !== undefined) params = params.set('per_page', mergedFilter.per_page.toString());

            const response = await this._httpClient
                .get<any>('aplt_backend/api/requests', { params })
                .toPromise();

            if (response) {
                const requests = response.content?.map((item: any) => this.mapRequestFromAPI(item)) || [];
                const requestsWithPatients = await Promise.all(
                    requests.map(request => this.loadPatientDataForRequest(request))
                );
                
                this._allRequests.set(requestsWithPatients);
                const pagination = convertPageToPagination(response);
                this._pagination.set(pagination);
                this._currentFilter.set(mergedFilter);
            }
        } catch (error) {
            console.error('Error loading expert contributions:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load expert contributions');
        } finally {
            this._isLoading.set(false);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Request Operations
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get request by ID
     */
    async getRequestById(id: string, markAsRead: boolean = false): Promise<ExpertRequest | null> {
        const existingRequest = this._allRequests().find(r => r.id === id || r.entityId === id);
        
        if (existingRequest && !markAsRead) {
            this._currentRequest.set(cloneDeep(existingRequest));
            return existingRequest;
        }

        this._isLoading.set(true);
        this._error.set(null);

        try {
            const expertId = await this.getExpertId();
            let params = new HttpParams().set('expert', expertId);

            const response = await this._httpClient
                .get<any>(`aplt_backend/api/requests/${id}`, { params })
                .toPromise();

            if (response) {
                const request = this.mapRequestFromAPI(response);
                const requestWithPatient = await this.loadPatientDataForRequest(request);
                
                if (markAsRead && !requestWithPatient.isRead) {
                    try {
                        await this.markAsRead(id);
                        requestWithPatient.isRead = true;
                    } catch (error) {
                        console.warn('Failed to mark request as read:', error);
                    }
                }
                
                this._currentRequest.set(cloneDeep(requestWithPatient));
                
                // Update in main list
                const requests = this._allRequests();
                const updatedRequests = requests.map(r => {
                    if (r.id === id || r.entityId === id) {
                        return requestWithPatient;
                    }
                    return r;
                });
                this._allRequests.set(updatedRequests);
                
                return requestWithPatient;
            }
            return null;
        } catch (error) {
            console.error('Error loading request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load request');
            return null;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Take charge of request (checkout) - WAITING -> IN_PROGRESS
     */
    async takeChargeOfRequest(requestId: string): Promise<ExpertRequest> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const expertId = await this.getExpertId();
            let params = new HttpParams().set('expertId', expertId);

            await this._httpClient
                .post(`aplt_backend/api/requests/${requestId}/checkout`, {}, { params })
                .toPromise();

            // Update local state
            const requests = this._allRequests();
            const updatedRequests = requests.map(r => {
                if (r.id === requestId) {
                    const updated = r.clone();
                    updated.status = 'IN_PROGRESS';
                    updated.isRead = true;
                    return updated;
                }
                return r;
            });
            
            this._allRequests.set(updatedRequests);
            
            const updatedRequest = updatedRequests.find(r => r.id === requestId);
            if (this._currentRequest()?.id === requestId && updatedRequest) {
                this._currentRequest.set(cloneDeep(updatedRequest));
            }

            return updatedRequest!;
        } catch (error) {
            console.error('Error taking charge of request:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to take charge of request');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Mark request as read
     */
    async markAsRead(requestId: string): Promise<void> {
        try {
            const expertId = await this.getExpertId();
            let params = new HttpParams().set('clinicianId', expertId);

            await this._httpClient
                .post(`aplt_backend/api/requests/${requestId}/read`, {}, { params })
                .toPromise();

            // Update local state
            this.updateRequestReadStatus(requestId, true);
        } catch (error) {
            console.error('Error marking request as read:', error);
            throw error;
        }
    }

    /**
     * Mark request as unread
     */
    async markAsUnread(requestId: string): Promise<void> {
        try {
            const expertId = await this.getExpertId();
            let params = new HttpParams().set('clinicianId', expertId);

            await this._httpClient
                .post(`aplt_backend/api/requests/${requestId}/unread`, {}, { params })
                .toPromise();

            // Update local state
            this.updateRequestReadStatus(requestId, false);
        } catch (error) {
            console.error('Error marking request as unread:', error);
            throw error;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Medical Opinion Management
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create medical opinion - send parameter in query (backend contract)
     */
    async createMedicalOpinion(requestId: string, opinionData: IMedicalOpinionCreate, attachment?: File): Promise<MedicalOpinion> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const expertId = await this.getExpertId();
            const formData = new FormData();

            // Backend expects: send as query param (boolean), opinion and attachment in FormData
            const send = opinionData.status === 'SENT';
            let params = new HttpParams()
                .set('expertId', expertId)
                .set('send', send.toString());

            formData.append('opinion', opinionData.opinion);
            if (attachment) {
                formData.append('attachment', attachment);
            }

            const response = await this._httpClient
                .post<any>(`aplt_backend/api/requests/${requestId}/medical-opinions`, formData, { params })
                .toPromise();

            if (!response) throw new Error('No response received from server');

            const opinion = this.mapOpinionFromAPI(response);
            this._currentOpinion.set(cloneDeep(opinion));

            // Update request state based on returned status
            await this.updateRequestStateAfterOpinion(requestId, opinion.status === 'SENT');

            return opinion;
        } catch (error) {
            console.error('Error creating medical opinion:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to create medical opinion');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Update medical opinion - send and opinion in query params, attachment in body (backend contract)
     */
    async updateMedicalOpinion(requestId: string, opinionId: string, opinionData: IMedicalOpinionCreate, attachment?: File): Promise<MedicalOpinion> {
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const expertId = await this.getExpertId();

            // Backend expects: send and opinion as query params, attachment in body (application/json)
            const send = opinionData.status === 'SENT';
            let params = new HttpParams()
                .set('expertId', expertId)
                .set('send', send.toString())
                .set('opinion', opinionData.opinion);

            const requestBody: any = {};
            if (attachment) {
                requestBody.attachment = attachment;
            }

            const response = await this._httpClient
                .put<any>(`aplt_backend/api/requests/${requestId}/medical-opinions/${opinionId}`, requestBody, { params })
                .toPromise();

            if (!response) throw new Error('No response received from server');

            const opinion = this.mapOpinionFromAPI(response);
            this._currentOpinion.set(cloneDeep(opinion));

            // Update request state based on returned status
            await this.updateRequestStateAfterOpinion(requestId, opinion.status === 'SENT');

            return opinion;
        } catch (error) {
            console.error('Error updating medical opinion:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to update medical opinion');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Get medical opinions for a request
     */
    async getMedicalOpinions(requestId: string): Promise<MedicalOpinion[]> {
        try {
            const response = await this._httpClient
                .get<any[]>(`aplt_backend/api/requests/${requestId}/medical-opinions`)
                .toPromise();

            if (response) {
                const opinions = response.map(item => this.mapOpinionFromAPI(item));
                this._allOpinions.set(opinions);
                return opinions;
            }
            return [];
        } catch (error) {
            console.error('Error loading medical opinions:', error);
            throw error;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Utility Methods
    // -----------------------------------------------------------------------------------------------------

    setCurrentRequest(request: ExpertRequest | null): void {
        this._currentRequest.set(request ? cloneDeep(request) : null);
    }

    setCurrentOpinion(opinion: MedicalOpinion | null): void {
        this._currentOpinion.set(opinion ? cloneDeep(opinion) : null);
    }

    async applyFilter(filter: IExpertRequestFilter): Promise<void> {
        const newFilter = { sort: 'zcreationTimestamp:desc', ...this._currentFilter(), ...filter };
        
        // Determine which load method to use based on current context
        if (newFilter.f_status === 'WAITING') {
            await this.loadPendingRequests(newFilter);
        } else {
            await this.loadExpertContributions(newFilter);
        }
    }

    async clearFilters(): Promise<void> {
        await this.loadPendingRequests({ sort: 'zcreationTimestamp:desc' });
    }

    clearData(): void {
        this._allRequests.set([]);
        this._currentRequest.set(null);
        this._allOpinions.set([]);
        this._currentOpinion.set(null);
        this._error.set(null);
        this._isLoading.set(false);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private Helper Methods
    // -----------------------------------------------------------------------------------------------------

    private updateRequestReadStatus(requestId: string, isRead: boolean): void {
        const requests = this._allRequests();
        const updatedRequests = requests.map(r => {
            if (r.id === requestId) {
                const updated = r.clone();
                updated.isRead = isRead;
                return updated;
            }
            return r;
        });
        
        this._allRequests.set(updatedRequests);
        
        const updatedRequest = updatedRequests.find(r => r.id === requestId);
        if (this._currentRequest()?.id === requestId && updatedRequest) {
            this._currentRequest.set(cloneDeep(updatedRequest));
        }
    }

    private async loadPatientDataForRequest(request: ExpertRequest): Promise<ExpertRequest> {
        if (!request.patient && request.patientId) {
            let patientData = this._patientCacheService.getPatient(request.patientId);
            
            if (!patientData) {
                try {
                    patientData = await this._patientService.getPatientById(request.patientId);
                    if (patientData) {
                        this._patientCacheService.setPatient(patientData);
                    }
                } catch (error) {
                    // console.warn(`Failed to load patient data for request ${request.id}:`, error);
                }
            }
            
            if (patientData) {
                const updatedRequest = request.clone();
                updatedRequest.patient = patientData.toDTO();
                return updatedRequest;
            }
        }
        return request;
    }

    private mapRequestFromAPI(apiRequest: any): ExpertRequest {
        return new ExpertRequest({
            entityId: apiRequest.entityId,
            zTouchTimestamp: apiRequest.zTouchTimestamp,
            zCreationTimestamp: apiRequest.zCreationTimestamp,
            status: apiRequest.status,
            speciality: apiRequest.speciality,
            requestType: apiRequest.requestType,
            content: apiRequest.content,
            isRead: apiRequest.isRead,
            clinicianId: apiRequest.clinicianId,
            patientId: apiRequest.patientId,
            handleBy: apiRequest.handleBy,
            clinician: apiRequest.clinician,
            patient: apiRequest.patient,
            expertAssigned: apiRequest.expertAssigned,
            requestStatusDates: apiRequest.requestStatusDates,
            ztouchTimestamp: apiRequest.ztouchTimestamp,
            zcreationTimestamp: apiRequest.zcreationTimestamp
        });
    }

    private mapOpinionFromAPI(apiOpinion: any): MedicalOpinion {
        return new MedicalOpinion({
            entityId: apiOpinion.entityId,
            zTouchTimestamp: apiOpinion.zTouchTimestamp,
            zCreationTimestamp: apiOpinion.zCreationTimestamp,
            opinion: apiOpinion.opinion,
            attachment: apiOpinion.attachment,
            status: apiOpinion.status,
            requestId: apiOpinion.requestId,
            expertId: apiOpinion.expertId,
            expert: apiOpinion.expert,
            requester: apiOpinion.requester,
            ztouchTimestamp: apiOpinion.ztouchTimestamp,
            zcreationTimestamp: apiOpinion.zcreationTimestamp
        });
    }

    /**
    * Update request state after opinion creation/update - RELOAD FROM BACKEND
    */
    private async updateRequestStateAfterOpinion(requestId: string, wasSent: boolean): Promise<void> {
        try {
            // Reload the request from backend to get the updated status
            const updatedRequest = await this.getRequestById(requestId, false);

            if (!updatedRequest) {
                console.warn('Failed to reload request after opinion update');
                return;
            }

            // Update drafts set based on status
            const requestsWithDraft = new Set(this._requestsWithDraft());

            // Check if there's a DRAFT opinion for this request
            try {
                const opinions = await this.getMedicalOpinions(requestId);
                const hasDraft = opinions.some(opinion => opinion.status === 'DRAFT');

                if (hasDraft) {
                    requestsWithDraft.add(requestId);
                } else {
                    requestsWithDraft.delete(requestId);
                }
            } catch (error) {
                console.warn('Error checking draft status:', error);
            }

            this._requestsWithDraft.set(requestsWithDraft);

            // Update the requests list with the reloaded request
            const requests = this._allRequests();
            const updatedRequests = requests.map(r => {
                if (r.id === requestId) {
                    return updatedRequest;
                }
                return r;
            });

            this._allRequests.set(updatedRequests);

            // Update current request if it's the same one
            if (this._currentRequest()?.id === requestId) {
                this._currentRequest.set(cloneDeep(updatedRequest));
            }
        } catch (error) {
            console.error('Error updating request state after opinion:', error);
        }
    }

    /**
    * Check if request has draft opinion (status = DRAFT)
    */
    async hasExistingDraftOpinion(requestId: string): Promise<boolean> {
        try {
            const opinions = await this.getMedicalOpinions(requestId);
            return opinions.some(opinion => opinion.status === 'DRAFT');
        } catch (error) {
            console.warn('Error checking for existing opinion:', error);
            return false;
        }
    }

    /**
    * Check if request has draft opinion
    */
    hasRequestDraft(requestId: string): boolean {
        return this._requestsWithDraft().has(requestId);
    }
}